import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../utils/apiConfig';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('pecash_token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // Extract serializable data immediately to avoid postMessage issues
      const responseOk = response.ok;
      const data = responseOk ? await response.json() : null;

      if (responseOk && data) {
        setUser(data.user);
        setWallet(data.wallet);
      } else {
        // Token invalid, clear it
        localStorage.removeItem('pecash_token');
        setToken(null);
        setUser(null);
        setWallet(null);
      }
    } catch (error) {
      // Ensure error is serializable before logging
      const errorMessage = error?.message || 'Unknown error';
      console.error('Failed to fetch user:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const sendMagicLink = async (email, role = 'sender') => {
    let responseData;
    let responseOk;
    let responseStatus;

    try {
      const response = await fetch(`${API_URL}/api/auth/magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, role }),
      });

      // Extract serializable data immediately to avoid postMessage issues
      // with non-clonable Response objects in iframe environments
      responseOk = response.ok;
      responseStatus = response.status;
      responseData = await response.json();
    } catch (networkError) {
      // Handle network errors with a plain serializable error
      throw new Error('Network error: Unable to connect to the server');
    }

    if (!responseOk) {
      const errorMessage = responseData?.detail || `Request failed with status ${responseStatus}`;
      throw new Error(errorMessage);
    }

    return responseData;
  };

  const verifyMagicLink = async (magicToken) => {
    let responseData;
    let responseOk;
    let responseStatus;

    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: magicToken }),
      });

      // Extract serializable data immediately to avoid postMessage issues
      // with non-clonable Response objects in iframe environments
      responseOk = response.ok;
      responseStatus = response.status;
      responseData = await response.json();
    } catch (networkError) {
      // Handle network errors with a plain serializable error
      throw new Error('Network error: Unable to connect to the server');
    }

    if (!responseOk) {
      const errorMessage = responseData?.detail || `Verification failed with status ${responseStatus}`;
      throw new Error(errorMessage);
    }

    localStorage.setItem('pecash_token', responseData.token);
    setToken(responseData.token);
    setUser(responseData.user);
    
    // Fetch wallet
    await fetchUser();
    
    return responseData;
  };

  const loginWithPassword = async (email, password) => {
    let responseData;
    let responseOk;
    let responseStatus;

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      responseOk = response.ok;
      responseStatus = response.status;
      responseData = await response.json();
    } catch (networkError) {
      throw new Error('Network error: Unable to connect to the server');
    }

    if (!responseOk) {
      const errorMessage = responseData?.detail || `Login failed with status ${responseStatus}`;
      throw new Error(errorMessage);
    }

    localStorage.setItem('pecash_token', responseData.token);
    setToken(responseData.token);
    setUser(responseData.user);
    
    // Fetch wallet
    await fetchUser();
    
    return responseData;
  };

  const logout = () => {
    localStorage.removeItem('pecash_token');
    setToken(null);
    setUser(null);
    setWallet(null);
  };

  const refreshWallet = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/wallets/my-wallet`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // Extract serializable data immediately
      const responseOk = response.ok;
      const walletData = responseOk ? await response.json() : null;
      
      if (responseOk && walletData) {
        setWallet(walletData);
      }
    } catch (error) {
      const errorMessage = error?.message || 'Unknown error';
      console.error('Failed to refresh wallet:', errorMessage);
    }
  };

  const value = {
    user,
    wallet,
    token,
    loading,
    isAuthenticated: !!user,
    sendMagicLink,
    verifyMagicLink,
    loginWithPassword,
    logout,
    refreshWallet,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
